import { ReactElement, RefObject, useEffect, useRef } from 'react';

import { ScrollToBottomCTA } from 'components/ScrollToBottomCTA';
import { FORCED_SYSTEM_TYPING_DURATION } from 'components/TypingIndicator/constants';

import { useAuthentication } from 'hooks/authentication';
import { useConversation } from 'hooks/conversation';
import { useView } from 'hooks/view';
import { BaseAuthorHeader } from './GetAuthorHeader';
import { ParseFormattedMessage } from './ParseFormattedMessage';
import {
  listenerMainContentScroll,
  observerAutoScroll,
  observerManualScroll,
} from './ScrollingObservers';
import WithDelayedAnimation from './WithDelayedAnimation/WithDelayedAnimation';

import { getLocalStorageItem } from 'utils/browserCache';
import { htmlId } from 'utils/html';
import { scrollToMessagesListBottom } from 'utils/message';
import { isScrollToBottomMsgEdgeCase } from './ScrollingObservers/utils';

import { LOCAL_STORAGE_KEY } from 'constants/browserCache';
import { ACTIONS } from 'constants/hook';
import { MESSAGES_LIST_BOTTOM_SPACER_ID } from 'constants/id';
import { PARTICIPANT_NAME_SYSTEM_FRIENDLY, PARTICIPANT_PREFIX } from 'constants/twilio';
import { TIME_BUFFER_ASYNC_SCROLL_UX, TIME_BUFFER_SCROLL_INIT } from './ScrollingObservers/config';
import {
  StyledMessagesContainer,
  StyledMessagesContent,
  StyledMessagesListBottomSpacer,
} from './styles';
import { FORM_TEMPLATE_TYPES } from 'constants/form';

type MessagesProps = {
  mainContentRef: RefObject<HTMLDivElement>;
  isForcedSystemTypingAnimation: boolean;
};

const Messages = ({
  mainContentRef,
  isForcedSystemTypingAnimation,
}: MessagesProps): ReactElement | null => {
  const { authenticationState } = useAuthentication();
  const { conversationState } = useConversation();
  const { dispatchViewAction, viewState } = useView();

  const initMsgCountRef = useRef<number>(
    getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_CHAT_UI)?.conversationMsgCount ?? 0,
  );
  const initScrollPosRef = useRef<number>(
    getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_CHAT_UI_SCROLL_POS) ?? 0,
  );
  const initIntersectionRatioRef = useRef<number>(0);
  const msgLastObsAutoScrollRef = useRef<IntersectionObserver | null>(null);
  const msgLastObsManualScrollInstanceRef = useRef<IntersectionObserver | null>(null);

  const twilioMessageCount = conversationState.messages.length;

  const handleScrollObservers = () => {
    const lastInit = authenticationState.unixTimeLastInitialization;
    const isJustInit = lastInit && Date.now() < lastInit + TIME_BUFFER_SCROLL_INIT;
    const lastMessage = conversationState.messages[twilioMessageCount - 1] ?? null;
    const isLastMsgByUser = lastMessage?.author.includes(PARTICIPANT_PREFIX.CUSTOMER) ?? false;
    const isAutoScrolling = Boolean(
      twilioMessageCount && viewState.scrollToBottom.isAutoScrollNextMsg,
    );
    const isAdvocateInConvo = conversationState.participants.advocates.length > 0;
    const isAdvocateInConvoAndLastMsgByUser = isAdvocateInConvo && isLastMsgByUser;

    const commonObsProps = {
      dispatchViewAction,
      isAdvocateInConvoAndLastMsgByUser,
      mainContentRef,
      msgCount: twilioMessageCount,
    };

    const nextViewState = { ...viewState };

    /** START scoped util functions **/
    const observerAutoScrollInstance = () => {
      observerAutoScroll({
        ...commonObsProps,
        isAdvocateInConvo,
        msgLastObsAutoScrollRef,
      });
    };
    const observerManualScrollInstance = () => {
      observerManualScroll({
        ...commonObsProps,
        dispatchViewAction,
        initMsgCountRef,
        initIntersectionRatioRef,
        isAutoScrolling,
        msgLastObsManualScrollInstanceRef,
        unixTimeLastInit: lastInit,
        unixTimeLastMsgReceived: conversationState.unixTimeLastMsgReceived,
      });
    };

    const handleInitAndEdgeCases = () => {
      setTimeout(() => {
        const prevSessionScrollPosition = getLocalStorageItem(
          LOCAL_STORAGE_KEY.CVNA_CHAT_UI_SCROLL_POS,
        );
        const isInitPositioning = isJustInit && typeof prevSessionScrollPosition === 'number';
        const isRatingRequest =
          lastMessage.extension?.form?.templateType === FORM_TEMPLATE_TYPES.RATING_REQUEST;

        if (!isInitPositioning && !isRatingRequest) {
          scrollToMessagesListBottom();
          nextViewState.scrollToBottom.isAutoScrollNextMsg = true;
        }
        dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
        observerManualScrollInstance();
      }, TIME_BUFFER_ASYNC_SCROLL_UX);
    };
    /** END scoped util functions **/

    if (isJustInit || isScrollToBottomMsgEdgeCase(lastMessage)) {
      handleInitAndEdgeCases();
      return;
    }
    if (isAutoScrolling) observerAutoScrollInstance();
    observerManualScrollInstance();
  };

  useEffect(() => {
    // Scroll listener to cache scroll position on re-entry of Chat app.
    // Don't need to unsubscribe. It will auto self-destruct and create on re-mount.
    mainContentRef.current?.addEventListener('scroll', (e) =>
      listenerMainContentScroll(initScrollPosRef, mainContentRef, dispatchViewAction),
    );
  }, []);

  useEffect(() => {
    const nextViewState = { ...viewState };
    nextViewState.scrollToBottom.hasSeenCTAThisMsg = false;
    dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
    handleScrollObservers();
  }, [conversationState.messages]);

  if (!twilioMessageCount) return null;

  return (
    <StyledMessagesContainer id={htmlId('chat-messages-container')}>
      <StyledMessagesContent id={htmlId('chat-messages-content')}>
        {conversationState.messages.map((message) => (
          <ParseFormattedMessage key={message.index} message={message} />
        ))}
        {isForcedSystemTypingAnimation && (
          <>
            <BaseAuthorHeader displayName={PARTICIPANT_NAME_SYSTEM_FRIENDLY} active={true} />
            <WithDelayedAnimation
              animate={true}
              isForcedSystemTypingAnimation={isForcedSystemTypingAnimation}
              duration={FORCED_SYSTEM_TYPING_DURATION}
            />
          </>
        )}
        <StyledMessagesListBottomSpacer id={htmlId(MESSAGES_LIST_BOTTOM_SPACER_ID)} />
      </StyledMessagesContent>
      <ScrollToBottomCTA />
    </StyledMessagesContainer>
  );
};

export default Messages;
